/**
 * AdvertiserOnboarding - Agent & Landlord Registration Flows
 * 
 * Separate onboarding experiences for:
 * - Estate/Letting Agents (business verification)
 * - Landlords/Property Owners (personal verification)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Building2, User, ArrowRight, ArrowLeft, Upload, CheckCircle2,
  Phone, Mail, MapPin, Globe, FileText, Shield, Camera,
  Loader2, AlertCircle, Home, Briefcase, BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, steps }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between mb-2">
      {steps.map((step, idx) => (
        <div key={idx} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            idx < currentStep 
              ? 'bg-green-500 text-white' 
              : idx === currentStep 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-500'
          }`}>
            {idx < currentStep ? <CheckCircle2 className="w-5 h-5" /> : idx + 1}
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-12 sm:w-20 h-1 mx-1 ${
              idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
    <div className="flex justify-between text-xs text-gray-500">
      {steps.map((step, idx) => (
        <span key={idx} className={`${idx === currentStep ? 'text-blue-600 font-medium' : ''}`}>
          {step}
        </span>
      ))}
    </div>
  </div>
);

// File upload component
const FileUpload = ({ label, accept, onChange, value, hint }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
      <input
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files[0])}
        className="hidden"
        id={`file-${label.replace(/\s/g, '-')}`}
      />
      <label htmlFor={`file-${label.replace(/\s/g, '-')}`} className="cursor-pointer">
        {value ? (
          <div className="flex items-center justify-center gap-2 text-green-600">
            <CheckCircle2 className="w-5 h-5" />
            <span>{value.name}</span>
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
            {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
          </>
        )}
      </label>
    </div>
  </div>
);

// Agent Onboarding Flow
const AgentOnboarding = ({ onComplete, onBack }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Business details
    companyName: '',
    registrationNumber: '',
    businessAddress: '',
    website: '',
    // Responsible person
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    position: '',
    // Branding
    logo: null,
    officePhone: '',
    officeLocation: '',
    description: '',
    // Documents
    registrationDoc: null,
    idDocument: null,
    // Agreements
    termsAccepted: false,
    dataProcessingAccepted: false
  });

  const steps = ['Business', 'Contact', 'Branding', 'Verify'];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create advertiser profile
      const response = await axios.post(`${API_URL}/api/advertisers/`, {
        name: formData.companyName,
        type: 'agent',
        email: formData.contactEmail,
        phone: formData.contactPhone,
        location: formData.officeLocation,
        business_name: formData.companyName,
        registration_number: formData.registrationNumber
      });

      const advertiserId = response.data.advertiser?.id;
      
      if (advertiserId) {
        // Upload logo if provided
        if (formData.logo) {
          const logoData = new FormData();
          logoData.append('file', formData.logo);
          try {
            await axios.post(`${API_URL}/api/advertisers/${advertiserId}/upload/logo`, logoData);
          } catch (uploadError) {
            console.error('Logo upload failed:', uploadError);
          }
        }
        
        // Upload business registration document
        if (formData.registrationDoc) {
          const regDocData = new FormData();
          regDocData.append('file', formData.registrationDoc);
          regDocData.append('document_type', 'business_registration');
          try {
            await axios.post(`${API_URL}/api/advertisers/${advertiserId}/upload/document`, regDocData);
          } catch (uploadError) {
            console.error('Registration doc upload failed:', uploadError);
          }
        }
        
        // Upload ID document
        if (formData.idDocument) {
          const idDocData = new FormData();
          idDocData.append('file', formData.idDocument);
          idDocData.append('document_type', 'government_id');
          try {
            await axios.post(`${API_URL}/api/advertisers/${advertiserId}/upload/document`, idDocData);
          } catch (uploadError) {
            console.error('ID document upload failed:', uploadError);
          }
        }
      }

      toast.success('Agent profile created! Verification pending.');
      onComplete(response.data);
    } catch (error) {
      console.error('Error creating agent profile:', error);
      toast.error('Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.companyName && formData.registrationNumber && formData.businessAddress;
      case 1: return formData.contactName && formData.contactEmail && formData.contactPhone;
      case 2: return formData.officeLocation;
      case 3: return formData.termsAccepted && formData.dataProcessingAccepted;
      default: return false;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <StepIndicator currentStep={step} totalSteps={4} steps={steps} />

      {/* Step 1: Business Details */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Business Details
            </CardTitle>
            <CardDescription>Tell us about your agency or company</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                placeholder="e.g., Andrea & Co Estate Agents"
                value={formData.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registrationNumber">Business Registration Number *</Label>
              <Input
                id="registrationNumber"
                placeholder="e.g., 12345678"
                value={formData.registrationNumber}
                onChange={(e) => updateField('registrationNumber', e.target.value)}
              />
              <p className="text-xs text-gray-500">UK Companies House number or equivalent</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessAddress">Registered Business Address *</Label>
              <Textarea
                id="businessAddress"
                placeholder="Full registered address"
                value={formData.businessAddress}
                onChange={(e) => updateField('businessAddress', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website (Optional)</Label>
              <Input
                id="website"
                placeholder="https://www.example.com"
                value={formData.website}
                onChange={(e) => updateField('website', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Contact Person */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Responsible Person
            </CardTitle>
            <CardDescription>Primary contact for verification</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">Full Name *</Label>
              <Input
                id="contactName"
                placeholder="Director or Manager name"
                value={formData.contactName}
                onChange={(e) => updateField('contactName', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <Input
                id="position"
                placeholder="e.g., Director, Branch Manager"
                value={formData.position}
                onChange={(e) => updateField('position', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="email@company.com"
                  value={formData.contactEmail}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone *</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  placeholder="+44 20 1234 5678"
                  value={formData.contactPhone}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Branding */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Branding & Office
            </CardTitle>
            <CardDescription>How you'll appear to property seekers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FileUpload
              label="Company Logo"
              accept="image/*"
              value={formData.logo}
              onChange={(file) => updateField('logo', file)}
              hint="Square image, min 200x200px"
            />
            <div className="space-y-2">
              <Label htmlFor="officePhone">Office Phone</Label>
              <Input
                id="officePhone"
                type="tel"
                placeholder="Main office number"
                value={formData.officePhone}
                onChange={(e) => updateField('officePhone', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="officeLocation">Office Location *</Label>
              <Input
                id="officeLocation"
                placeholder="e.g., Harrow, London"
                value={formData.officeLocation}
                onChange={(e) => updateField('officeLocation', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">About Your Agency</Label>
              <Textarea
                id="description"
                placeholder="Brief description of your services..."
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Verification */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Verification Documents
            </CardTitle>
            <CardDescription>Upload documents to verify your business</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <FileUpload
              label="Business Registration Certificate"
              accept=".pdf,.jpg,.jpeg,.png"
              value={formData.registrationDoc}
              onChange={(file) => updateField('registrationDoc', file)}
              hint="Companies House certificate or equivalent"
            />
            <FileUpload
              label="Director ID Document"
              accept=".pdf,.jpg,.jpeg,.png"
              value={formData.idDocument}
              onChange={(file) => updateField('idDocument', file)}
              hint="Passport or driving licence"
            />

            <div className="pt-4 border-t space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={formData.termsAccepted}
                  onCheckedChange={(checked) => updateField('termsAccepted', checked)}
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  I agree to the <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> and confirm the information provided is accurate
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="dataProcessing"
                  checked={formData.dataProcessingAccepted}
                  onCheckedChange={(checked) => updateField('dataProcessingAccepted', checked)}
                />
                <label htmlFor="dataProcessing" className="text-sm text-gray-600">
                  I consent to the processing of submitted documents for verification purposes
                </label>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <BadgeCheck className="w-4 h-4" />
                What happens next?
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Your documents will be reviewed within 2-3 business days</li>
                <li>• You'll receive email confirmation once verified</li>
                <li>• You can start listing properties immediately (marked as pending verification)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => step === 0 ? onBack() : setStep(step - 1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {step < 3 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canProceed() || loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Complete Registration
          </Button>
        )}
      </div>
    </div>
  );
};

// Landlord Onboarding Flow
const LandlordOnboarding = ({ onComplete, onBack }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Personal details
    fullName: '',
    email: '',
    phone: '',
    address: '',
    // Identity verification
    idDocument: null,
    selfiePhoto: null,
    // Property ownership
    ownershipDeclaration: false,
    // Agreements
    termsAccepted: false
  });

  const steps = ['Personal', 'Verify', 'Complete'];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/advertisers/`, {
        name: formData.fullName,
        type: 'landlord',
        email: formData.email,
        phone: formData.phone,
        location: formData.address
      });

      const advertiserId = response.data.advertiser?.id;
      
      if (advertiserId) {
        // Upload ID document
        if (formData.idDocument) {
          const idDocData = new FormData();
          idDocData.append('file', formData.idDocument);
          idDocData.append('document_type', 'government_id');
          try {
            await axios.post(`${API_URL}/api/advertisers/${advertiserId}/upload/document`, idDocData);
          } catch (uploadError) {
            console.error('ID document upload failed:', uploadError);
          }
        }
        
        // Upload proof of ownership
        if (formData.ownershipProof) {
          const ownershipDocData = new FormData();
          ownershipDocData.append('file', formData.ownershipProof);
          ownershipDocData.append('document_type', 'property_ownership');
          try {
            await axios.post(`${API_URL}/api/advertisers/${advertiserId}/upload/document`, ownershipDocData);
          } catch (uploadError) {
            console.error('Ownership proof upload failed:', uploadError);
          }
        }
      }

      toast.success('Landlord profile created! You can now list properties.');
      onComplete(response.data);
    } catch (error) {
      console.error('Error creating landlord profile:', error);
      toast.error('Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.fullName && formData.email && formData.phone;
      case 1: return formData.idDocument && formData.ownershipDeclaration;
      case 2: return formData.termsAccepted;
      default: return false;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <StepIndicator currentStep={step} totalSteps={3} steps={steps} />

      {/* Step 1: Personal Details */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-green-600" />
              Personal Details
            </CardTitle>
            <CardDescription>Tell us about yourself</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                placeholder="As shown on your ID"
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+94 77 123 4567"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Your Address</Label>
              <Textarea
                id="address"
                placeholder="Your residential address"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Identity Verification */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              Identity Verification
            </CardTitle>
            <CardDescription>Verify your identity to build trust</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <FileUpload
              label="ID Document *"
              accept=".pdf,.jpg,.jpeg,.png"
              value={formData.idDocument}
              onChange={(file) => updateField('idDocument', file)}
              hint="Passport, driving licence, or national ID"
            />
            
            <FileUpload
              label="Selfie Photo (Optional)"
              accept="image/*"
              value={formData.selfiePhoto}
              onChange={(file) => updateField('selfiePhoto', file)}
              hint="Clear photo of your face for verification"
            />

            <div className="pt-4 border-t">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="ownership"
                  checked={formData.ownershipDeclaration}
                  onCheckedChange={(checked) => updateField('ownershipDeclaration', checked)}
                />
                <label htmlFor="ownership" className="text-sm text-gray-600">
                  I declare that I own or have legal authority to advertise the properties I will list on Zeilian
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Complete */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Almost Done!
            </CardTitle>
            <CardDescription>Review and complete your registration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{formData.fullName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{formData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{formData.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">ID Document:</span>
                <span className="font-medium text-green-600">
                  {formData.idDocument ? 'Uploaded' : 'Not provided'}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={formData.termsAccepted}
                onCheckedChange={(checked) => updateField('termsAccepted', checked)}
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I agree to the <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
              </label>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">What you can do:</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• List your properties immediately</li>
                <li>• Receive enquiries from potential tenants</li>
                <li>• Track views and manage listings</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => step === 0 ? onBack() : setStep(step - 1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {step < 2 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="bg-green-600 hover:bg-green-700"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canProceed() || loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Complete Registration
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Onboarding Page
export default function AdvertiserOnboarding() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);

  const handleComplete = (advertiser) => {
    // Store advertiser ID and redirect
    localStorage.setItem('advertiser_id', advertiser.advertiser?.id || advertiser.id);
    navigate('/post-ad');
  };

  // Type selection screen
  if (!selectedType) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12" data-testid="advertiser-onboarding">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Start Advertising on Zeilian
            </h1>
            <p className="text-lg text-gray-600">
              Who are you advertising as?
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Agent/Company Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-blue-300 group"
              onClick={() => setSelectedType('agent')}
              data-testid="select-agent"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Building2 className="w-10 h-10 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Estate / Letting Agent
                </h2>
                <p className="text-gray-600 mb-4">
                  Manage multiple properties for clients
                </p>
                <ul className="text-sm text-gray-500 space-y-1 text-left">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Business verification available
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Multiple property listings
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Company branding
                  </li>
                </ul>
                <Button className="mt-6 w-full bg-blue-600 hover:bg-blue-700">
                  Register as Agent
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Landlord Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:border-green-300 group"
              onClick={() => setSelectedType('landlord')}
              data-testid="select-landlord"
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-2xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Home className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Landlord / Property Owner
                </h2>
                <p className="text-gray-600 mb-4">
                  Advertise your own property
                </p>
                <ul className="text-sm text-gray-500 space-y-1 text-left">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Quick & simple setup
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Identity verification
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Direct tenant contact
                  </li>
                </ul>
                <Button className="mt-6 w-full bg-green-600 hover:bg-green-700">
                  Register as Landlord
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Already registered? <a href="/login" className="text-blue-600 hover:underline">Sign in to your account</a>
          </p>
        </div>
      </div>
    );
  }

  // Render appropriate onboarding flow
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <Badge className={selectedType === 'agent' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
            {selectedType === 'agent' ? 'Agent Registration' : 'Landlord Registration'}
          </Badge>
        </div>

        {selectedType === 'agent' ? (
          <AgentOnboarding onComplete={handleComplete} onBack={() => setSelectedType(null)} />
        ) : (
          <LandlordOnboarding onComplete={handleComplete} onBack={() => setSelectedType(null)} />
        )}
      </div>
    </div>
  );
}
